import { useState, type ReactNode, type Ref } from 'react';
import { cssVar } from '@going-once/tokens';
import { Icon, type IconName } from './icons';
import { Menu, type MenuGroup } from './menu';
import { accentColor, focusRing, Pressable, Text, type Accent } from './primitives';
import { type TabItem, Tabs } from './tabs';

/**
 * A staff console's frame: top bar, side nav, the main column, a page header and the
 * placeholder every page is today. Shared by the admin console and the seller dashboard —
 * only the accent differs.
 *
 * From the design system's `AppShell`, `WebTopBar` and `PageHeader` (Kelli's `Going Once
 * Admin`). What is left out of PR 1 is left out on purpose: no search and no bell (nothing
 * backs them yet — search stays in scope), no account menu (a "Log out" button stands in),
 * no collapsible rail.
 */

/** "Skip to content": the first tab stop, invisible until it has focus. */
export function SkipLink() {
  const [focused, setFocused] = useState(false);
  return (
    <a
      // A plain in-page link: `main` takes focus because it has `tabIndex={-1}`.
      href="#main"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={
        focused
          ? {
              position: 'fixed',
              top: cssVar('space.sm'),
              left: cssVar('space.sm'),
              zIndex: 30,
              padding: `${cssVar('space.sm')} ${cssVar('space.md')}`,
              borderRadius: cssVar('radius.md'),
              background: cssVar('color.bg.raised'),
              color: cssVar('color.fg.default'),
              fontFamily: cssVar('family.base'),
              fontSize: cssVar('type.body.size'),
              ...focusRing,
            }
          : {
              // Visually hidden, still in the tab order and the accessibility tree.
              position: 'absolute',
              width: 1,
              height: 1,
              overflow: 'hidden',
              clipPath: 'inset(50%)',
              whiteSpace: 'nowrap',
            }
      }
    >
      Skip to content
    </a>
  );
}

/** GOING ONCE, with ONCE in the accent. The design's teal bolt is not drawn (MAPPING_TO_CONFIRM). */
function Wordmark({ onHome }: { onHome: () => void }) {
  const [focused, setFocused] = useState(false);
  return (
    <a
      href="/"
      aria-label="Going Once home"
      onClick={(event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        onHome();
      }}
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={{
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        fontFamily: cssVar('family.base'),
        fontSize: cssVar('type.heading.size'),
        fontWeight: cssVar('type.heading.weight'),
        fontStyle: cssVar('type.heading.style'),
        lineHeight: cssVar('type.heading.line'),
        color: cssVar('color.fg.default'),
        ...(focused ? focusRing : {}),
      }}
    >
      GOING <span style={{ color: cssVar('color.accent.default') }}>ONCE</span>
    </a>
  );
}

/**
 * The app badge. The design draws white on the fill, which is 3.4:1 on pink and fails AA
 * for text this size, so it is the tested pair instead — `fg.inverse` on the accent.
 */
function AppBadge({ accent, children }: { accent: Accent; children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: `${cssVar('space.xs')} ${cssVar('space.sm')}`,
        borderRadius: cssVar('radius.full'),
        background: accentColor(accent),
      }}
    >
      <Text step="label" tone="fg.inverse">
        {children}
      </Text>
    </span>
  );
}

export function TopBar({
  badge,
  accent = 'accent.default',
  who,
  onHome,
  onSignOut,
}: {
  badge: string;
  /** Defaults to the admin console's pink, which was the only console when this was written. */
  accent?: Accent;
  /** The signed-in person, as the token names them. */
  who: string | undefined;
  onHome: () => void;
  onSignOut: () => void;
}) {
  return (
    <header
      style={{
        gridColumn: '1 / -1',
        display: 'flex',
        alignItems: 'center',
        gap: cssVar('space.md'),
        boxSizing: 'border-box',
        height: cssVar('size.bar.top'),
        padding: `0 ${cssVar('space.lg')}`,
        background: cssVar('color.bg.bar'),
        borderBottom: `1px solid ${cssVar('color.border.default')}`,
      }}
    >
      <Wordmark onHome={onHome} />
      <AppBadge accent={accent}>{badge}</AppBadge>
      <span style={{ flex: 1 }} />
      {who ? (
        <Text step="label" tone="fg.muted" truncate>
          {who}
        </Text>
      ) : null}
      <Pressable variant="quiet" onPress={onSignOut}>
        Log out
      </Pressable>
    </header>
  );
}

/**
 * A breadcrumb: a plain label (a nav section, which is a dropdown and not a page), or a
 * link back to a page. `href` is the page's own address, so the link can be opened in a
 * new tab or copied the browser's way; `onPress` does an ordinary click's navigating,
 * because what it leaves can be state rather than a URL (the seller detail is the list
 * page with a seller selected).
 */
export type Crumb = string | { label: string; href: string; onPress: () => void };

const crumbLabel = (crumb: Crumb) => (typeof crumb === 'string' ? crumb : crumb.label);

export interface HeaderAction {
  label: string;
  onPress: () => void;
  icon?: IconName;
}

/**
 * The top of a page (the design system's `PageHeader`). A list page is the title alone —
 * the Sellers list draws nothing else. A record page adds, in the design's order: linked
 * crumbs back to its list; a `lead` (the avatar); a `badge` beside the title; a `meta`
 * line under it; `secondary` actions as quiet buttons; the rest behind ••• (`more`, which
 * draws nothing when it holds no items); and `tabs` under the whole row.
 *
 * One `h1` per page, in the display step, uppercase, on one line with the full title on
 * hover. `children` is a line under the title that belongs to the page as a whole (the
 * audit log's reason for reading it).
 */
export function PageHeader({
  title,
  crumbs,
  lead,
  badge,
  meta,
  secondary,
  more,
  tabs,
  titleRef,
  children,
}: {
  title: string;
  crumbs: Crumb[];
  lead?: ReactNode;
  badge?: ReactNode;
  meta?: string | undefined;
  secondary?: readonly HeaderAction[] | undefined;
  more?: readonly MenuGroup[] | undefined;
  tabs?: { items: readonly TabItem[]; value: string; onChange: (id: string) => void; idBase: string } | undefined;
  /** The heading takes focus from the caller (not a tab stop), for a page opened by a press: focus lands on what opened. */
  titleRef?: Ref<HTMLHeadingElement>;
  children?: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: cssVar('space.sm') }}>
      {crumbs.length > 0 ? (
        <nav aria-label="Breadcrumb">
          <ol
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              gap: cssVar('space.xs'),
              color: cssVar('color.fg.muted'),
            }}
          >
            {crumbs.map((crumb, index) => {
              const last = index === crumbs.length - 1;
              return (
                <li
                  key={crumbLabel(crumb)}
                  style={{ display: 'flex', alignItems: 'center', gap: cssVar('space.xs'), minWidth: 0 }}
                >
                  {typeof crumb !== 'string' && !last ? (
                    <CrumbLink crumb={crumb} />
                  ) : (
                    <span aria-current={last ? 'page' : undefined}>
                      <Text step="label" tone={last ? 'fg.default' : 'fg.muted'}>
                        {crumbLabel(crumb)}
                      </Text>
                    </span>
                  )}
                  {last ? null : <Icon name="chevron-right" />}
                </li>
              );
            })}
          </ol>
        </nav>
      ) : null}
      <div style={{ display: 'flex', alignItems: 'center', gap: cssVar('space.lg'), minWidth: 0 }}>
        {lead ? <span style={{ display: 'flex', flexShrink: 0 }}>{lead}</span> : null}
        <div style={{ display: 'flex', flexDirection: 'column', gap: cssVar('space.xs'), flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: cssVar('space.md'), minWidth: 0 }}>
            <h1
              title={title}
              {...(titleRef && { ref: titleRef, tabIndex: -1 })}
              style={{
                ...(titleRef ? { outline: 'none' } : {}),
                margin: 0,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
                color: cssVar('color.fg.default'),
                fontFamily: cssVar('family.base'),
                fontSize: cssVar('type.display.size'),
                fontWeight: cssVar('type.display.weight'),
                fontStyle: cssVar('type.display.style'),
                lineHeight: cssVar('type.display.line'),
              }}
            >
              {title}
            </h1>
            {badge ? <span style={{ display: 'flex', flexShrink: 0 }}>{badge}</span> : null}
          </div>
          {meta ? (
            <Text step="label" tone="fg.muted" truncate>
              {meta}
            </Text>
          ) : null}
        </div>
        {secondary?.length || more?.length ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: cssVar('space.sm'), flexShrink: 0 }}>
            {secondary?.map((action) => (
              <Pressable key={action.label} variant="quiet" onPress={action.onPress}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: cssVar('space.xs') }}>
                  {action.icon ? <Icon name={action.icon} /> : null}
                  {action.label}
                </span>
              </Pressable>
            ))}
            {more ? <Menu label="More actions" groups={more} /> : null}
          </div>
        ) : null}
      </div>
      {children}
      {tabs ? (
        <div style={{ marginTop: cssVar('space.sm') }}>
          <Tabs
            label="Page sections"
            tabs={tabs.items}
            value={tabs.value}
            onChange={tabs.onChange}
            idBase={tabs.idBase}
          />
        </div>
      ) : null}
    </div>
  );
}

function CrumbLink({ crumb }: { crumb: Exclude<Crumb, string> }) {
  const [focused, setFocused] = useState(false);
  return (
    <a
      href={crumb.href}
      onClick={(event) => {
        // A modified click is the browser's: a new tab, a new window.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        crumb.onPress();
      }}
      onFocus={(event) => setFocused(event.currentTarget.matches(':focus-visible'))}
      onBlur={() => setFocused(false)}
      style={{
        color: cssVar('color.fg.muted'),
        textDecoration: 'underline',
        borderRadius: cssVar('radius.sm'),
        ...(focused ? focusRing : {}),
      }}
    >
      <Text step="label" tone="fg.muted">
        {crumb.label}
      </Text>
    </a>
  );
}

/** What every blank console page is until a team designs it. The copy is the design's, verbatim. */
export function Placeholder({ title }: { title: string }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: cssVar('space.sm'),
        padding: cssVar('space.2xl'),
        borderRadius: cssVar('radius.lg'),
        border: `1px dashed ${cssVar('color.border.strong')}`,
        color: cssVar('color.fg.muted'),
        textAlign: 'center',
      }}
    >
      <Icon name="layout-template" />
      <Text step="title">{title}</Text>
      <Text tone="fg.muted">This page hasn't been designed yet.</Text>
    </div>
  );
}

/**
 * **A console surface, pinned dark.**
 *
 * Both consoles are specified dark-only, and saying so settles nothing on its own:
 * `main.tsx` puts `data-theme="light"` on `html` whenever the OS prefers light, and on
 * the resulting light palette `bg.sheet` is the same white as `bg.base` and the teal
 * accent on `bg.bar` is 1.90:1. `tokensToCss()` emits a `[data-theme='dark']` rule for
 * this to win with — before that rule existed the attribute could only say "light here",
 * which is why nesting alone could not have fixed it (D46 addendum, 2026-10-12).
 *
 * It is meant to wrap a console's refusals as well as its frame: a notice on the wrong
 * palette is the same bug with less of it on screen.
 */
export function Dark({ children }: { children: ReactNode }) {
  return (
    <div data-theme="dark" style={{ minHeight: '100vh', background: cssVar('color.bg.base') }}>
      {children}
    </div>
  );
}

/**
 * Top bar across, side nav down the left, and a main column that scrolls vertically and
 * never horizontally, capped at `size.container.wide`.
 */
export function AppFrame({ top, nav, children }: { top: ReactNode; nav: ReactNode; children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: `${cssVar('size.bar.top')} minmax(0, 1fr)`,
        gridTemplateColumns: `${cssVar('size.sidebar')} minmax(0, 1fr)`,
        height: '100vh',
        background: cssVar('color.bg.base'),
      }}
    >
      <SkipLink />
      {top}
      {nav}
      <main id="main" tabIndex={-1} style={{ overflowY: 'auto', overflowX: 'hidden', outline: 'none' }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: cssVar('space.lg'),
            boxSizing: 'border-box',
            maxWidth: cssVar('size.container.wide'),
            margin: '0 auto',
            padding: `${cssVar('space.lg')} ${cssVar('space.xl')} ${cssVar('space.2xl')}`,
          }}
        >
          {children}
        </div>
      </main>
    </div>
  );
}
